import {
  validateMaterialsInvestigationConfig,
  type MaterialsInvestigationConfig,
} from './MaterialsInvestigationConfig.js';
import {
  MaterialsInvestigationState,
  type MaterialChoiceResult,
  type MaterialObservationRecord,
  type MaterialsInvestigationSnapshot,
} from './MaterialsInvestigationState.js';

export interface MaterialsInvestigationEvents {
  readonly onStateChanged?: (snapshot: MaterialsInvestigationSnapshot) => void;
  readonly onMaterialInspected?: (
    materialId: string,
    snapshot: MaterialsInvestigationSnapshot,
  ) => void;
  readonly onTestRun?: (
    observation: MaterialObservationRecord,
    snapshot: MaterialsInvestigationSnapshot,
  ) => void;
  readonly onObservationRecorded?: (
    observation: MaterialObservationRecord,
    snapshot: MaterialsInvestigationSnapshot,
  ) => void;
  readonly onChoiceMade?: (
    result: MaterialChoiceResult,
    snapshot: MaterialsInvestigationSnapshot,
  ) => void;
  readonly onCompleted?: (
    result: MaterialChoiceResult,
    config: MaterialsInvestigationConfig,
    snapshot: MaterialsInvestigationSnapshot,
  ) => void;
}

/**
 * Rendering-independent Test & Build engine. Content defines materials,
 * simplified visual tests, evidence requirements, purpose, and feedback.
 */
export class MaterialsInvestigation {
  private readonly state: MaterialsInvestigationState;
  private completionEmitted = false;

  constructor(
    readonly config: MaterialsInvestigationConfig,
    private readonly events: MaterialsInvestigationEvents = {},
    initialSnapshot?: MaterialsInvestigationSnapshot,
  ) {
    validateMaterialsInvestigationConfig(config);
    this.state = new MaterialsInvestigationState(config, initialSnapshot);
    this.completionEmitted = this.state.isComplete;
  }

  getSnapshot() { return this.state.snapshot(); }

  inspectMaterial(materialId: string) {
    const changed = this.state.inspectMaterial(materialId);
    if (!changed) return false;
    const snapshot = this.state.snapshot();
    this.events.onMaterialInspected?.(materialId, snapshot);
    this.events.onStateChanged?.(snapshot);
    return true;
  }

  beginPrediction() { return this.change(() => this.state.beginPrediction()); }
  choosePrediction(materialId: string) {
    return this.change(() => this.state.choosePrediction(materialId));
  }

  runTest(materialId: string, testId: string) {
    const observation = this.state.runTest(materialId, testId);
    if (!observation) return undefined;
    const snapshot = this.state.snapshot();
    this.events.onTestRun?.(observation, snapshot);
    this.events.onStateChanged?.(snapshot);
    return observation;
  }

  recordObservation() {
    const observation = this.state.recordObservation();
    if (!observation) return undefined;
    const snapshot = this.state.snapshot();
    this.events.onObservationRecorded?.(observation, snapshot);
    this.events.onStateChanged?.(snapshot);
    return observation;
  }

  beginComparison() { return this.change(() => this.state.beginComparison()); }
  beginMaterialChoice() { return this.change(() => this.state.beginMaterialChoice()); }

  chooseMaterial(materialId: string) {
    const result = this.state.chooseMaterial(materialId);
    if (!result) return undefined;
    const snapshot = this.state.snapshot();
    this.events.onChoiceMade?.(result, snapshot);
    this.events.onStateChanged?.(snapshot);
    return result;
  }

  applyMaterial() {
    const changed = this.state.applyMaterial();
    if (!changed) return false;
    const snapshot = this.state.snapshot();
    this.events.onStateChanged?.(snapshot);
    const result = snapshot.choice;
    if (!this.completionEmitted && result) {
      this.completionEmitted = true;
      this.events.onCompleted?.(result, this.config, snapshot);
    }
    return true;
  }

  requestHint() {
    const hint = this.state.requestHint();
    if (hint) this.events.onStateChanged?.(this.state.snapshot());
    return hint;
  }

  reset() {
    this.state.reset();
    this.completionEmitted = false;
    this.events.onStateChanged?.(this.state.snapshot());
  }

  private change(operation: () => boolean) {
    const changed = operation();
    if (changed) this.events.onStateChanged?.(this.state.snapshot());
    return changed;
  }
}
